import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Pagination from '../components/Pagination/Pagination';
import usePagination from '../components/Pagination/usePagination';
import { useEventsStore } from '../features/events/state/use-events-store';
import { useEventTypesStore } from '../features/events/state/use-event-types-store';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { EventBusy, Schedule, InfoOutlined, SignalCellularAltOutlined, ReportProblemOutlined } from '@mui/icons-material';
import { getDevicesByQuery, getUsersByQuery } from '../api/api';
import MemoryIcon from '@mui/icons-material/Memory';
import Tooltip from '../components/Tooltip';
import ArrowForward from '@mui/icons-material/ArrowForward';
import { useRedirectToAccount } from '../features/user/useRedirectToAccount';
import SearchBar from '../components/SearchBar/SearchBar';
import { Table, TableTitle } from '../components';

const LIMIT_EVENTS = 10;
const INITIAL_VALUES = { userId: '', deviceId: '', message: '', categories: [], levels: [], from: null, to: null };

const EventsTable = () => {
  const { page, limit, updatePageUrl } = usePagination({ limit: LIMIT_EVENTS });
  const { data, error, isLoading, total, fetchCompanyEvents } = useEventsStore();
  const { categories, levels, fetchEventTypes, messagesError } = useEventTypesStore();
  const [searchValue, setSearchValue] = useState(INITIAL_VALUES);
  const { handleSwitchToUserAccount } = useRedirectToAccount();

  useEffect(() => {
    const get = async () => {
      await fetchEventTypes();
    };
    get();
  }, []);

  useEffect(() => {
    const get = async () => {
      await fetchCompanyEvents(page, limit, searchValue);
    };

    get();
  }, [page, limit, searchValue]);

  const totalPages = Math.ceil(total / limit);

  const handleSearch = useCallback(
    (value, name) => {
      setSearchValue((prev) => ({ ...prev, [name]: value }));
      updatePageUrl(1);
    },
    [updatePageUrl]
  );

  const loadUserOptions = (field) => async (inputValue) => {
    const users = await getUsersByQuery({ [field]: inputValue });
    return users.data.map((user) => ({
      label: user[field],
      value: user?.id,
    }));
  };

  const loadDeviceOptions = async (inputValue) => {
    const devices = await getDevicesByQuery({ serial_number: inputValue });
    return devices.data.map((device) => ({
      label: device.serial_number,
      value: device.id,
    }));
  };

  const inputs = useMemo(
    () => [
      {
        placeholderText: 'Дата начала',
        type: 'date',
        selected: searchValue.from,
        onChange: (value) => handleSearch(value, 'from'),
        prefix: <Schedule fontSize={'small'} className="text-gray-500 mr-2" />,
      },
      {
        placeholderText: 'Дата окончания',
        type: 'date',
        selected: searchValue.to,
        onChange: (value) => handleSearch(value, 'to'),
        prefix: <EventBusy fontSize={'small'} className="text-gray-500 mr-2" />,
      },
      {
        placeholder: 'Поиск по ФИО...',
        type: 'search',
        handleSearch: (value) => handleSearch(value, 'userId'),
        prefix: <PersonIcon fontSize={'small'} className={'text-gray-500'} />,
        loadOptions: loadUserOptions('fio'),
      },
      {
        placeholder: 'Поиск по телефону...',
        type: 'search',
        handleSearch: (value) => handleSearch(value, 'userId'),
        prefix: <PhoneIcon fontSize={'small'} className={'text-gray-500'} />,
        loadOptions: loadUserOptions('phone'),
      },
      {
        placeholder: 'Поиск по Email...',
        type: 'search',
        handleSearch: (value) => handleSearch(value, 'userId'),
        prefix: <EmailIcon fontSize={'small'} className={'text-gray-500'} />,
        loadOptions: loadUserOptions('email'),
      },
      {
        placeholder: 'Поиск по устройству...',
        type: 'search',
        handleSearch: (value) => handleSearch(value, 'deviceId'),
        prefix: <MemoryIcon fontSize={'small'} className="text-gray-500" />,
        loadOptions: loadDeviceOptions,
      },
      {
        placeholder: 'По сообщению ошибки',
        type: 'search',
        handleSearch: (value) => handleSearch(value, 'message'),
        prefix: <ReportProblemOutlined fontSize={'small'} className={'text-gray-500'} />,
        defaultOptions: messagesError,
      },
      {
        label: 'По категориям',
        options: categories,
        selectedOptions: searchValue.categories,
        type: 'filter',
        setSelectedOptions: (value) => handleSearch(value, 'categories'),
        prefix: <SignalCellularAltOutlined fontSize={'small'} className={'text-gray-500'} />,
      },
      {
        label: 'По уровню',
        options: levels,
        selectedOptions: searchValue.levels,
        type: 'filter',
        setSelectedOptions: (value) => handleSearch(value, 'levels'),
        prefix: <InfoOutlined fontSize={'small'} className={'text-gray-500'} />,
      },
    ],
    [categories, handleSearch, levels, messagesError, searchValue.categories, searchValue.from, searchValue.levels, searchValue.to]
  );

  const columns = useMemo(
    () => [
      {
        key: 'user',
        title: 'Пользователь',
        render: (value) => value?.fio,
      },
      {
        key: 'device',
        title: 'Адрес',
        render: (value) => `${value?.address}, кв. ${value?.apartment}`,
      },
      {
        key: 'device',
        title: 'Устройство',
        render: (value) => `${value?.serialNumber}`,
      },
      {
        key: 'code',
        title: 'Код ошибки',
      },
      {
        key: 'name',
        title: 'Описание',
        render: (value) => <div className={'w-[150px]'}>{value}</div>,
      },
      {
        key: 'category',
        title: 'Категория',
      },
      {
        key: 'level',
        title: 'Уровень',
        render: (value) => <div className={'text-center'}>{levels?.[value]?.icon}</div>,
      },
      {
        key: 'payload',
        title: 'Сообщение',
        render: (value) => <div className={'w-[200px]'}>{value?.message}</div>,
      },
      {
        key: 'occurred',
        title: 'Время',
        render: (value) => new Date(value).toLocaleString('ru-RU'),
      },
      {
        key: 'actions',
        title: 'Действия',
        render: (row) => (
          <div className={'text-center flex items-center justify-center'}>
            <Tooltip text="Перейти в ЛК">
              <button
                onClick={() => handleSwitchToUserAccount(row?.user?.id)}
                className="p-1 flex items-center justify-center rounded-full border border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white transition">
                <ArrowForward />
              </button>
            </Tooltip>
          </div>
        ),
      },
    ],
    [handleSwitchToUserAccount, levels]
  );

  return (
    <div className="bg-gray-100 min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        <TableTitle title={'События'} />
        <SearchBar inputs={inputs} />
      </div>
      <Table columns={columns} data={data} loading={isLoading} error={error}>
        {isLoading || error || totalPages === 0 ? null : <Pagination totalPages={totalPages} withLimit={true} />}
      </Table>
    </div>
  );
};

export default EventsTable;
